// Theme-derived palette helpers for the Prediction tab. Previously a set of
// hard-coded hexes (#6B5CE7 brand-purple + warm-mid/warm-low thresholds) that did
// NOT track the user's chosen theme. Now every value resolves from `theme` so the
// PBJ prediction surface retints with the rest of the app:
//   - the brand accent  → theme.palette.primary   (was #6B5CE7)
//   - the mid threshold  → theme.palette.warning    (2-star, warm orange)
//   - the low threshold  → theme.palette.error      (1-star, red)
//   - the "on target"     → theme.palette.success   (met-goal green)
// Shared so PredictionHero, SetTargetDialog, CategoryBreakdownTable,
// RecommendationsSidebar and ActionItems all read one cohesive language.
import type { Theme } from "@mui/material/styles";
import { alpha, lighten } from "@mui/material/styles";
import { FONT_SIZE, NEUTRAL, RADIUS, SHADOW, SURFACE } from "../../../theme/tokens";

/** Alpha weights for the PBJ soft surfaces (chips, banners, tinted panels). */
export const PBJ_ALPHA = {
	/** Solid-ish tinted fill (selected chip, badge background). */
	TINT: 0.14,
	/** Soft wash (banner / dialog highlight box background). */
	WASH: 0.06,
	/** Border on a tinted surface. */
	BORDER: 0.24,
	/** Faint hover / suggested-row wash. */
	FAINT: 0.04,
} as const;

/**
 * Corner radii for the PBJ surfaces, as multiples of the theme's 8px `shape.borderRadius` (the unit a
 * numeric `borderRadius` takes in `sx`), derived from the app-wide `RADIUS` scale so they follow it.
 */
export const PBJ_RADIUS = {
	/** A top-level card on the tab (the panels, the hero) — the focal-panel radius, 24px. */
	CARD: RADIUS.HERO / RADIUS.CARD,
	/** A callout inset inside a card (the verdict, the projected-result box) — 16px. */
	INSET: RADIUS.POPOVER / RADIUS.CARD,
	/** A bordered control (an icon affordance, the period trigger) — 12px. */
	CONTROL: RADIUS.CONTROL / RADIUS.CARD,
} as const;

/** The brand accent used across the tab — resolves to the user's chosen primary. */
export const pbjAccent = (theme: Theme): string => theme.palette.primary.main;

/** Rating-color scale: 1★ error, 2★ warning, 3★+ brand accent. Functional signaling. */
export const ratingColor = (theme: Theme, rating: number): string => {
	if (rating <= 1) return theme.palette.error.main;
	if (rating <= 2) return theme.palette.warning.main;
	return theme.palette.primary.main;
};

/**
 * Brand fill for the breakdown meters. The bar shows each measure's points/max as a plain "how
 * full" indicator with NO good/bad grading — the health color ramp (green→teal→amber→red) and its
 * tier labels were removed by product direction.
 */
export const meterFill = (theme: Theme): string => theme.palette.primary.main;

/** Soft brand-tinted fill for chips / selected states / badges. */
export const brandTint = (theme: Theme): string => alpha(theme.palette.primary.main, PBJ_ALPHA.TINT);
/** Very soft brand wash for banner / highlight-box backgrounds. */
export const brandWash = (theme: Theme): string => alpha(theme.palette.primary.main, PBJ_ALPHA.WASH);
/** Faint brand wash for a suggested (not-yet-added) row. */
export const brandFaint = (theme: Theme): string => alpha(theme.palette.primary.main, PBJ_ALPHA.FAINT);
/** Border for a brand-tinted surface. */
export const brandBorder = (theme: Theme): string => alpha(theme.palette.primary.main, PBJ_ALPHA.BORDER);

/** Soft warning (mid / below-target) fill + border for the target banner. */
export const midWash = (theme: Theme): string => alpha(theme.palette.warning.main, PBJ_ALPHA.WASH);
export const midTint = (theme: Theme): string => alpha(theme.palette.warning.main, PBJ_ALPHA.TINT);
export const midBorder = (theme: Theme): string => alpha(theme.palette.warning.main, PBJ_ALPHA.BORDER);

/** Soft success (on-target / met-goal) fill + border. */
export const highWash = (theme: Theme): string => alpha(theme.palette.success.main, PBJ_ALPHA.WASH);
export const highTint = (theme: Theme): string => alpha(theme.palette.success.main, PBJ_ALPHA.TINT);
export const highBorder = (theme: Theme): string => alpha(theme.palette.success.main, PBJ_ALPHA.BORDER);

/** Neutral surfaces shared by the tables / panel headers / progress tracks. */
export const PANEL_HEADER_BG = NEUTRAL[50];
/** Table header row fill — the SAME neutral as the sub-tab pill track (`subTabsPillSx`), so the
 * chrome above a grid and the grid's own header band read as one family. */
export const TABLE_HEAD_BG = NEUTRAL[150];
export const ROW_OPEN_BG = NEUTRAL[50];
export const TRACK_BG = NEUTRAL[150];
export const STAR_EMPTY = NEUTRAL[200];
export const HAIRLINE = NEUTRAL[200];

// ─────────────────────────────────────────────────────────────────────────────
// Bold redesign: the "dark premium hero" surface + its ink/line tokens, the dark
// action-plan card, and the tinted soft shadows for the light working area. Every
// value derives from `theme` so the whole PBJ surface retints when the org changes
// its brand color (the hero is just a darkened primary, the same family as the prod
// header gradient) — nothing here is a frozen hex.
// ─────────────────────────────────────────────────────────────────────────────

/** Alpha weights for the dark-hero glass/ink layers (translucent white on the dark stage). */
const HERO_ALPHA = {
	/** Brand-light radial glow strength (top-left). */
	GLOW: 0.34,
	/** Tertiary-gold radial glow strength (bottom-right — barely-there warmth). */
	GLOW_GOLD: 0.09,
	/** Hairline / divider on the dark stage. */
	LINE: 0.14,
	/** Unfilled meter/gauge track on the dark stage. */
	TRACK: 0.14,
	/** Ghost-button border. */
	GHOST_BORDER: 0.26,
	/** Ghost-button fill. */
	GHOST_FILL: 0.07,
	/** Glass pill fill (context row). */
	GLASS: 0.08,
	/** Muted ink (secondary text) on the dark stage. */
	INK_MUTED: 0.76,
} as const;

/**
 * DEFAULT: the dark-hero stage — the one genuinely new visual value in this redesign.
 * A darkened-primary gradient in the same family as the prod header (primary.dark →
 * primary[900]), overlaid with a soft brand-light glow (top-left) and a faint gold
 * warmth (bottom-right). The 55% mid-stop is `primary[800]` so custom themes get a
 * smooth three-stop ramp too (not just the default #4F517A → #343247). Derived from
 * `theme`, so it retints with the org's brand color while never looking like a default.
 */
export const heroSurface = (theme: Theme): string => {
	const glow = alpha(theme.palette.primary.light, HERO_ALPHA.GLOW);
	const gold = alpha(theme.palette.tertiary.main, HERO_ALPHA.GLOW_GOLD);
	return [
		`radial-gradient(120% 140% at 18% 15%, ${glow}, transparent 45%)`,
		`radial-gradient(90% 120% at 92% 90%, ${gold}, transparent 50%)`,
		`linear-gradient(140deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary[800]} 55%, ${theme.palette.primary[900]} 100%)`,
	].join(", ");
};

/** Primary ink (headings, the big score) on the dark hero. */
export const heroInk = (): string => SURFACE;
/** Muted ink (eyebrows, captions, secondary lines) on the dark hero. */
export const heroInkMuted = (): string => alpha(SURFACE, HERO_ALPHA.INK_MUTED);
/** Hairline / divider on the dark hero (context-row top border, etc.). */
export const heroLine = (): string => alpha(SURFACE, HERO_ALPHA.LINE);
/** Unfilled gauge/meter track on the dark hero. */
export const heroTrack = (): string => alpha(SURFACE, HERO_ALPHA.TRACK);
/** Ghost-button border on the dark hero (Set target / Manual hours). */
export const heroGhostBorder = (): string => alpha(SURFACE, HERO_ALPHA.GHOST_BORDER);
/** Ghost-button fill on the dark hero. */
export const heroGhostFill = (): string => alpha(SURFACE, HERO_ALPHA.GHOST_FILL);
/** Ghost-button hover fill on the dark hero (a touch stronger than rest). */
export const heroGhostHover = (): string => alpha(SURFACE, HERO_ALPHA.GHOST_FILL * 2);
/** Translucent glass pill fill for the context row (Facility / State / period). */
export const heroGlassPill = (): string => alpha(SURFACE, HERO_ALPHA.GLASS);
/** The hero's accent (eyebrow, progress chip, "Change period") — the bright brand stop. */
export const heroAccent = (theme: Theme): string => theme.palette.primary.light;
/** The dark-hero primary CTA fill: bright brand with deep-primary text (reads as a lit button on the dark stage). */
export const heroCtaFill = (theme: Theme): string => theme.palette.primary.light;
export const heroCtaText = (theme: Theme): string => theme.palette.primary[900];
/** Progress-to-target fill on the hero — deep→bright brand ramp with a soft glow. */
export const heroProgressFill = (theme: Theme): string => `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`;

/**
 * DEFAULT: the hatched "projected / with-plan" fill for the hero progress bar and any
 * projected extension on the dark stage — a 45° diagonal stripe alternating a stronger and
 * fainter brand-light so the with-plan segment reads as forecast (not-yet-real), distinct
 * from the solid "now" fill. Derived from `primary.light`, so it retints with the brand.
 * The two alpha stops mirror the approved mock's `repeating-linear-gradient` hatch.
 */
export const heroProjectedHatch = (theme: Theme): string => {
	const strong = alpha(theme.palette.primary.light, 0.55);
	const faint = alpha(theme.palette.primary.light, 0.26);
	return `repeating-linear-gradient(45deg, ${strong} 0 6px, ${faint} 6px 12px)`;
};

/** The gold used ONLY on the gauge stars + the gauge target tick (tertiary brand). Never on buttons. */
export const goldAccent = (theme: Theme): string => theme.palette.tertiary.main;

/**
 * How far a signal color is lightened toward white to stay legible ON the dark hero stage.
 * Tuned so the two accent inks below clear WCAG AA against the gradient's lightest stop.
 */
const HERO_INK_LIGHTEN = 0.45;

/**
 * Success ink readable ON the dark hero stage (the hero verdict's "target reached" line). The raw
 * `success.main` is too dark on the deep-primary gradient, so it's lightened toward white.
 * Theme-derived, so it retints with the palette.
 */
export const heroSuccessInk = (theme: Theme): string => lighten(theme.palette.success.main, HERO_INK_LIGHTEN);

/**
 * `heroAccent` as INK — for accent-colored TEXT on the dark stage. The raw `primary.light` stop is
 * only ~2.9:1 against the gradient's lightest stop (`primary.dark`), which fails AA at the label
 * sizes used here; lightened toward white it clears it while keeping the brand hue that ties an
 * accent number to the projected/with-plan fill. Use `heroAccent` for fills, this for text.
 */
export const heroAccentInk = (theme: Theme): string => lighten(theme.palette.primary.light, HERO_INK_LIGHTEN);

/**
 * DEFAULT: the dark action-plan card (inside the "How" panel) — a deep primary.dark →
 * primary[900] gradient with light text, echoing the hero so the "now → projected →
 * target" flow reads as the same instrument family. Theme-derived; retints with brand.
 */
export const actionPlanSurface = (theme: Theme): string => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary[900]})`;

/** Alpha weights for the primary-dark-tinted soft card shadows (depth, not palette). */
const SHADOW_ALPHA = {
	INK: 0.05,
	CARD: 0.1,
	FLOAT: 0.18,
} as const;

/**
 * DEFAULT: soft, primary-dark-tinted elevation for the light working area — replaces the
 * flat 1px hairline cards with real depth. Two levels: `cardShadow` for the Why/How panels
 * + data-table cards, `floatShadow` for the hero. Derived from `primary.dark` so the tint
 * tracks the brand (a blue theme casts a blue-tinted shadow, etc.).
 */
export const cardShadow = (theme: Theme): string => {
	const ink = alpha(NEUTRAL[900], SHADOW_ALPHA.INK);
	const brand = alpha(theme.palette.primary.dark, SHADOW_ALPHA.CARD);
	return `0 1px 2px ${ink}, 0 8px 26px ${brand}`;
};
export const floatShadow = (theme: Theme): string => {
	const ink = alpha(NEUTRAL[900], SHADOW_ALPHA.INK + 0.01);
	const brand = alpha(theme.palette.primary.dark, SHADOW_ALPHA.FLOAT);
	return `0 2px 6px ${ink}, 0 16px 40px ${brand}`;
};

/**
 * Pill-row treatment for a `SystemTabs` used as a sub-tab bar (Audit / Submission). Restyles
 * the underline-indicator tab bar into the mockup's segmented pill row: a soft neutral track,
 * no indicator, and a brand-tinted fill on the active tab. Kept as a shared `sx` so every PBJ
 * sub-tab bar reads identically. Brand-derived (active fill + text follow the theme primary).
 *
 * `onCanvas` is for a bar that sits directly on the page canvas instead of inside a white card
 * (the Audit tab): the neutral track is within ~2% of the canvas tint and would vanish there, so
 * it switches to a lifted white track.
 */
export const subTabsPillSx = (theme: Theme, { onCanvas = false }: { onCanvas?: boolean } = {}) =>
	({
		minHeight: 0,
		alignSelf: "flex-start",
		maxWidth: "100%",
		bgcolor: onCanvas ? SURFACE : TABLE_HEAD_BG,
		...(onCanvas && { boxShadow: cardShadow(theme) }),
		borderRadius: "12px",
		p: 0.625,
		"& .MuiTabs-indicator": { display: "none" },
		"& .MuiTabs-flexContainer": { gap: 0.375 },
		"& .MuiTab-root": {
			minHeight: 34,
			borderRadius: "8px",
			px: 1.5,
			py: 0.75,
			color: theme.palette.text.secondary,
			transition: "background-color 150ms, color 150ms",
			// Bold, unmistakable active tab — a solid brand pill (not a washed tint) so the current tab reads at a glance.
			"&.Mui-selected": { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 700, boxShadow: SHADOW.PANEL_SOFT },
			"&.Mui-selected:hover": { bgcolor: theme.palette.primary.dark },
			"@media (prefers-reduced-motion: reduce)": { transition: "none" },
		},
	}) as const;

/**
 * Shared `sx` for a `SettingsSection` used as a PBJ content section (the Audit tab's sub-tabs) —
 * re-skins the primitive's hairline-bordered box into the PBJ card language.
 */
export const sectionCardSx = (theme: Theme) =>
	({
		border: "none",
		borderRadius: 3,
		boxShadow: cardShadow(theme),
	}) as const;

/**
 * Shared `sx` for a data-table `SystemDataGrid` on the PBJ data tabs — the mockup's uppercase,
 * muted, tint-backed headers with hairline rows, a soft brand hover, and tabular right-aligned
 * numeric cells. Layered over the grid's base sx; brand-derived so the header tint + hover
 * track the theme primary. Every grid on these tabs uses it, so the header row reads the same
 * everywhere.
 */
export const dataTableSx = (theme: Theme) =>
	({
		border: "none",
		"& .MuiDataGrid-columnHeader": {
			bgcolor: TABLE_HEAD_BG,
			color: theme.palette.text.secondary,
		},
		// Styled on the title CONTAINER, not just `-columnHeaderTitle`: a sortable column's label is a
		// custom `renderHeader` (SortableDataGridHeader) that never renders that inner span, so
		// title-only rules skipped every sortable column and left the header row half-uppercased.
		"& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitle": {
			color: theme.palette.text.primary,
			fontWeight: 700,
			fontSize: FONT_SIZE.LABEL_XXS,
			letterSpacing: "0.06em",
			textTransform: "uppercase",
		},
		"& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": { bgcolor: TABLE_HEAD_BG },
		"& .MuiDataGrid-container--top": { bgcolor: theme.palette.background.paper },
		"& .MuiDataGrid-cell": { borderBottom: `1px solid ${NEUTRAL[200]}` },
		"& .MuiDataGrid-row:hover": { backgroundColor: alpha(theme.palette.primary.main, PBJ_ALPHA.FAINT) },
		// Clear brand-tinted selection with a bold left accent rail — replaces MUI's barely-there default gray.
		"& .MuiDataGrid-row.Mui-selected": {
			backgroundColor: alpha(theme.palette.primary.main, PBJ_ALPHA.TINT),
			boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
		},
		"& .MuiDataGrid-row.Mui-selected:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
	}) as const;
